import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import React from 'react'
import { PaytrieAPI } from '@paytrie/client'

import { Button } from '@/components/Button/Button'
import { LoadingIcon } from '@/components/LoadingIcon'
import { ALLOWED_REDIRECT_PATHS } from '@/constants/login'
import { usePageStateStore } from '@/reducers'
import {
  approveMagicLink,
  denyMagicLink,
  generateSessionId,
  getMagicLinkDetails,
  MagicLinkUnauthorizedError,
} from '@paytrie/common/auth'
import { PanelLayout } from './PanelLayout'
import { track } from '@/utils/analytics'

type ViewState =
  | { kind: 'loading' }
  | { kind: 'review'; details: PaytrieAPI.MagicLinkDetails }
  | { kind: 'submitting' }
  | { kind: 'signedIn' }
  | { kind: 'denied' }
  | { kind: 'expired' }
  | { kind: 'error' }

const REDIRECT_COUNTDOWN_SECONDS = 10

const remainingSeconds = (expiresAt: string) =>
  Math.max(0, dayjs(expiresAt).diff(dayjs(), 'second'))

const formatMmSs = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
}

type ExpiresCountdownProps = {
  expiresAt: string
  onExpire: () => void
}

const ExpiresCountdownInner = ({
  expiresAt,
  onExpire,
}: ExpiresCountdownProps) => {
  const [remaining, setRemaining] = useState(() =>
    formatMmSs(remainingSeconds(expiresAt))
  )

  useEffect(() => {
    const tick = () => {
      const seconds = remainingSeconds(expiresAt)
      setRemaining(formatMmSs(seconds))
      if (seconds === 0) {
        window.clearInterval(intervalId)
        onExpire()
      }
    }
    const intervalId = window.setInterval(tick, 1000)
    return () => window.clearInterval(intervalId)
  }, [expiresAt, onExpire])

  return <>{remaining}</>
}

const ExpiresCountdown = React.memo(ExpiresCountdownInner)

const LoginVerifyPanelInner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const setAuthToken = usePageStateStore.use.setAuthToken()
  const setLoggedInUser = usePageStateStore.use.setEmail()
  const setProtectedRouteRedirectPath =
    usePageStateStore.use.setProtectedRouteRedirectPath()
  const queryParameters = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  )
  const email = queryParameters.get('email') || ''
  const id = queryParameters.get('id') || ''
  const redirectPath = useMemo(() => {
    const raw = queryParameters.get('redirectpath') || ''
    return ALLOWED_REDIRECT_PATHS.includes(raw) ? raw : '/'
  }, [queryParameters])
  const goToApp = useCallback(() => {
    setProtectedRouteRedirectPath('')
    navigate(redirectPath, { replace: true })
  }, [navigate, redirectPath, setProtectedRouteRedirectPath])
  const onRedirectLinkKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      if (e.key === 'Enter' || e.key === ' ') goToApp()
    },
    [goToApp]
  )

  const [view, setView] = useState<ViewState>({ kind: 'loading' })
  const [redirectIn, setRedirectIn] = useState(REDIRECT_COUNTDOWN_SECONDS)
  const [sessionId] = useState(() => generateSessionId())

  useEffect(() => {
    try {
      window.clarity?.('set', 'paytrie_session_id', sessionId)
    } catch {
      //
    }
  }, [sessionId])

  useEffect(() => {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Loading magic link details',
      level: 'info',
      data: { email, id },
    })

    getMagicLinkDetails({ email, verifier: id })
      .then((details) => setView({ kind: 'review', details }))
      .catch((error: unknown) => {
        if (error instanceof MagicLinkUnauthorizedError) {
          setView({ kind: 'expired' })
          return
        }
        Sentry.captureException(error, {
          tags: { component: 'LoginVerifyPanel', action: 'getDetails' },
        })
        setView({ kind: 'error' })
      })
  }, [email, id])

  const handleApprove = useCallback(async () => {
    setView({ kind: 'submitting' })
    try {
      const { accessToken } = await approveMagicLink({
        email,
        verifier: id,
        sessionId,
      })
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Magic link approved and token issued',
        level: 'info',
        data: { email },
      })
      setAuthToken(accessToken)
      setLoggedInUser(email)
      track('login_approved')
      setView({ kind: 'signedIn' })
    } catch (error: unknown) {
      if (error instanceof MagicLinkUnauthorizedError) {
        setView({ kind: 'expired' })
        return
      }
      Sentry.captureException(error, {
        tags: { component: 'LoginVerifyPanel', action: 'approve' },
      })
      setView({ kind: 'error' })
    }
  }, [email, id, sessionId, setAuthToken, setLoggedInUser])

  useEffect(() => {
    if (view.kind !== 'signedIn') return
    if (redirectIn <= 0) {
      goToApp()
      return
    }
    const timer = window.setTimeout(
      () => setRedirectIn((prev) => prev - 1),
      1000
    )
    return () => window.clearTimeout(timer)
  }, [view.kind, redirectIn, goToApp])

  const handleExpire = useCallback(() => {
    setView({ kind: 'expired' })
  }, [])

  const handleDeny = useCallback(async () => {
    setView({ kind: 'submitting' })
    try {
      await denyMagicLink({ email, verifier: id })
      track('login_denied')
      setView({ kind: 'denied' })
    } catch (error: unknown) {
      if (error instanceof MagicLinkUnauthorizedError) {
        setView({ kind: 'expired' })
        return
      }
      Sentry.captureException(error, {
        tags: { component: 'LoginVerifyPanel', action: 'deny' },
      })
      setView({ kind: 'error' })
    }
  }, [email, id])

  if (view.kind === 'loading' || view.kind === 'submitting') {
    return (
      <PanelLayout titleKey="welcome.title">
        <div className="flex justify-center" style={{ paddingLeft: '135px' }}>
          <LoadingIcon />
        </div>
      </PanelLayout>
    )
  }

  if (view.kind === 'review') {
    const { details } = view
    return (
      <PanelLayout titleKey="welcome.title">
        <div
          data-testid="container-login-verify-review"
          className="flex flex-col gap-5"
        >
          <h2 className="text-base font-normal uppercase">
            {t('verify.approve.title')}
          </h2>
          <p className="text-base">{t('verify.approve.intro')}</p>
          <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            <dt className="font-medium">{t('verify.approve.ipLabel')}</dt>
            <dd data-testid="text-login-verify-ip">{details.requestIp}</dd>
            {details.requestLocation && (
              <>
                <dt className="font-medium">
                  {t('verify.approve.locationLabel')}
                </dt>
                <dd data-testid="text-login-verify-location">
                  {details.requestLocation}
                </dd>
              </>
            )}
            <dt className="font-medium">
              {t('verify.approve.requestedAtLabel')}
            </dt>
            <dd data-testid="text-login-verify-requested-at">
              {dayjs(details.requestedAt).format('MMM D, YYYY h:mm A')}
            </dd>
            <dt className="font-medium">
              {t('verify.approve.expiresAtLabel')}
            </dt>
            <dd data-testid="text-login-verify-expires-at">
              <ExpiresCountdown
                expiresAt={details.expiresAt}
                onExpire={handleExpire}
              />
            </dd>
          </dl>
          <div className="grid grid-cols-2 gap-3">
            <Button
              color="secondary"
              onClick={handleDeny}
              data-testid="btn-login-verify-deny"
              className="inline-flex items-center py-8 px-4 w-full justify-center !bg-transparent !border-black dark:!border-snow !text-black dark:!text-snow"
            >
              {t('verify.approve.denyButton')}
            </Button>
            <Button
              onClick={handleApprove}
              data-testid="btn-login-verify-approve"
              className="inline-flex items-center py-8 px-4 w-full justify-center"
            >
              {t('verify.approve.approveButton')}
            </Button>
          </div>
        </div>
      </PanelLayout>
    )
  }

  const [title, status] = (() => {
    switch (view.kind) {
      case 'signedIn':
        return [
          t('verify.success.title'),
          t('verify.success.status', { countdown: redirectIn }),
        ]
      case 'denied':
        return [t('verify.denied.title'), t('verify.denied.status')]
      case 'expired':
        return [t('verify.expired.title'), t('verify.expired.status')]
      case 'error':
      default:
        return ['', t('verify.error.status')]
    }
  })()

  return (
    <PanelLayout titleKey="welcome.title">
      <div data-testid="container-login-verify" className="flex flex-col gap-5">
        <h2
          data-testid="text-login-verify-title"
          className="text-base font-normal uppercase"
        >
          {title}
        </h2>
        <div data-testid="text-login-verify-status" className="text-base">
          {status}
        </div>
        {view.kind === 'signedIn' && (
          <div
            data-testid="container-login-verify-redirect"
            className="text-base mt-2"
          >
            {t('verify.redirect.text')}{' '}
            <span
              role="button"
              tabIndex={0}
              data-testid="link-login-verify-redirect"
              className="underline text-blue-600 cursor-pointer"
              onClick={goToApp}
              onKeyDown={onRedirectLinkKeyDown}
            >
              {t('verify.redirect.link')}
            </span>
            .
          </div>
        )}
      </div>
    </PanelLayout>
  )
}

export const LoginVerifyPanel = React.memo(LoginVerifyPanelInner)
